import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import type { UseQueryReturnType } from '../@types';
import { gqlClient } from '../clients';
import { graphql } from '../gql';
import type {
  Tasks_CancelTaskInput,
  Tasks_MyTasksFilter,
} from '../gql/graphql';
import { Scheduling_JobStatus } from '../gql/graphql';
import { queryClient } from '../query-client';
import { coreApiKeys } from './keys';

const tasksQuery = graphql(/* GraphQL */ `
  query tasks_tasks($filter: Tasks_MyTasksFilter) {
    tasks_tasks(filter: $filter) {
      executionId
      handlerName
      status
      submittedAt
      startedAt
      finishedAt
      durationMs
      successCount
      failCount
      exceptionMessage
      taskResults {
        type
        title
        payload
      }
    }
  }
`);

const cancelTaskMutation = graphql(/* GraphQL */ `
  mutation tasks_cancelTask($input: Tasks_CancelTaskInput!) {
    tasks_cancelTask(input: $input) {
      success
    }
  }
`);

const TERMINAL_STATUSES = new Set([
  Scheduling_JobStatus.Success,
  Scheduling_JobStatus.Failed,
  Scheduling_JobStatus.PartialSuccess,
  Scheduling_JobStatus.Cancelled,
]);

export function isTerminalStatus(
  status: Scheduling_JobStatus | undefined | null,
): boolean {
  return status != null && TERMINAL_STATUSES.has(status);
}

const tasksQueryConfig = (filter: Tasks_MyTasksFilter) => ({
  queryKey: coreApiKeys.tasks.list(filter),
  queryFn: async () => {
    const { tasks_tasks } = await gqlClient.request(tasksQuery, { filter });
    return tasks_tasks;
  },
});

export function getRecentTasksFilter(): Tasks_MyTasksFilter {
  return {
    from: dayjs().subtract(24, 'hour').startOf('hour').toISOString(),
  };
}

export function useMyTasks(filter: Tasks_MyTasksFilter) {
  return useQuery(tasksQueryConfig(filter));
}

export function getMyTasks(filter: Tasks_MyTasksFilter) {
  return queryClient.fetchQuery(tasksQueryConfig(filter));
}

export function useTrackedTasks(executionIds: string[]) {
  const previousDataRef = useRef<string | null>(null);

  const trackedQuery = useQuery({
    queryKey: coreApiKeys.tasks.tracked(executionIds),
    queryFn: async () => {
      const { tasks_tasks } = await gqlClient.request(tasksQuery, {
        filter: { executionIds },
      });
      return tasks_tasks;
    },
    enabled: executionIds.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const tasks = query.state.data;
      if (!tasks || tasks.length === 0) return false;
      const allTerminal = tasks.every((task) => isTerminalStatus(task.status));
      return allTerminal ? false : 5000;
    },
  });

  useEffect(() => {
    if (!trackedQuery.data) return;

    const snapshot = trackedQuery.data.map((task) => task.status).join(',');
    if (previousDataRef.current !== snapshot) {
      previousDataRef.current = snapshot;
      queryClient.invalidateQueries({
        queryKey: coreApiKeys.tasks.allLists(),
      });
    }
  }, [trackedQuery.data]);

  return trackedQuery;
}

export function useCancelTask() {
  return useMutation({
    mutationFn: (input: Tasks_CancelTaskInput) =>
      gqlClient.request(cancelTaskMutation, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreApiKeys.tasks.all() });
    },
  });
}

export type ReturnTypeFromUseMyTasks = UseQueryReturnType<
  typeof useMyTasks
>[number];
